import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AppointmentService } from '@modules/sales/services/appointment.service';
import { TaskService } from './task.service';
import { KfmProcessType } from '@shared/enums/process-type.enum';
import { Task } from '../models/task.model';
import { KfmSalesSelectionDialogComponent } from '../features/sales/sales-selection-dialog/kfm-sales-selection-dialog.component';
import { Dialog } from '@angular/cdk/dialog';
import { DatePipe } from '@angular/common';
import { TaskTypeService } from './task-type.service';
import {
  OFFER_PAGE,
  TASK_PAGE,
  INVOICE_PAGE,
} from '@shared/constants/routing-constants';
import { RailNavService } from '@shared/ui/services/rail-nav.service';
import {
  QUERY_PARAM_DATE_FILTER,
  QUERY_PARAM_SALES_ID,
  QUERY_PARAM_TAB_INDEX,
} from '@shared/constants/query-param-constants';

@Injectable({ providedIn: 'root' })
export class ConvertSalesItemService {
  private readonly _router = inject(Router);
  private readonly _taskStore = inject(TaskService);
  private readonly _appointmentStore = inject(AppointmentService);
  private readonly _taskTypeService = inject(TaskTypeService);
  private readonly _dialog = inject(Dialog);
  private readonly _railNavService = inject(RailNavService);
  private readonly _datePipe = new DatePipe('de-DE');

  public async convertProcess(original: Task): Promise<void> {
    if (original === null) return;

    if (this._taskTypeService.isAppointment(original)) {
      await this.convertAppointmentToProcess(original);
    } else {
      await this.convertProcessToProcess(original);
    }
  }

  private async convertProcessToProcess(original: Task) {
    const dialogRef = this._dialog.open<KfmProcessType>(
      KfmSalesSelectionDialogComponent,
      {
        width: '600px',
        maxWidth: '90vw',
        maxHeight: '90vh',
        data: {
          processType: original.type,
        },
      },
    );

    dialogRef.closed.subscribe(async (result) => {
      if (result) {
        const processId: string | undefined = await this._taskStore.convert(
          original.uId,
          result,
        );

        if (!!processId && processId.trim().length > 0) {
          await this.navigateToProcess(processId, result);
        }
      }
    });
  }

  private async convertAppointmentToProcess(original: Task) {
    // Make sure everything is in a transaction
    const taskId: string | undefined = await this._appointmentStore.convert(
      original.uId,
    );

    if (!!taskId && taskId.trim().length > 0) {
      await this.navigateToTask(taskId);
    }
  }

  private async navigateToProcess(
    processId: string | undefined,
    processType: KfmProcessType,
  ) {
    const filter: string | null = this._datePipe.transform(
      new Date(),
      'yyyy-MM-dd',
    );

    const page =
      processType === KfmProcessType.Offer
        ? OFFER_PAGE
        : processType === KfmProcessType.Order
          ? TASK_PAGE
          : INVOICE_PAGE;

    if (page == TASK_PAGE) {
      await this._router.navigate([page], {
        queryParams: {
          [QUERY_PARAM_TAB_INDEX]: 1,
          [QUERY_PARAM_SALES_ID]: processId,
          [QUERY_PARAM_DATE_FILTER]: filter,
        },
      });
    } else {
      await this._router.navigate([page], {
        queryParams: {
          [QUERY_PARAM_SALES_ID]: processId,
          [QUERY_PARAM_DATE_FILTER]: filter,
        },
      });
    }
    this._railNavService.setActiveByRoute(page);
  }

  private async navigateToTask(taskId: string) {
    const filter: string | null = this._datePipe.transform(
      new Date(),
      'yyyy-MM-dd',
    );

    await this._router.navigate([TASK_PAGE], {
      queryParams: {
        [QUERY_PARAM_TAB_INDEX]: 0,
        [QUERY_PARAM_SALES_ID]: taskId,
        [QUERY_PARAM_DATE_FILTER]: filter,
      },
    });
    this._railNavService.setActiveByRoute(TASK_PAGE);
  }
}
